import React, { useState, useRef, useEffect } from 'react';
import { regulationService } from '../services/regulationService';

interface RegulationsHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Source with document type and section number */
interface QASource {
  documentType: string;
  sectionNumber: string;
}

/** Cached answer for repeated questions */
interface CachedAnswer {
  answer: string;
  sources: Array<{ documentType: string; sectionNumber: string }>;
  tokenUsage?: { input: number; output: number; total: number };
  cost?: number;
  queryTimeMs?: number;
  timing?: { loadSections: number; generateEmbedding: number; similaritySearch: number; claudeResponse: number; total: number };
}

/**
 * Modal for asking natural language questions about Special Regulations.
 * Uses Claude via Bedrock to answer questions with citations.
 */
const RegulationsHelpModal: React.FC<RegulationsHelpModalProps> = ({ isOpen, onClose }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [sources, setSources] = useState<QASource[]>([]);
  const [tokenUsage, setTokenUsage] = useState<{ input: number; output: number; total: number } | null>(null);
  const [cost, setCost] = useState<number | null>(null);
  const [queryTimeMs, setQueryTimeMs] = useState<number | null>(null);
  const [timing, setTiming] = useState<{ loadSections: number; generateEmbedding: number; similaritySearch: number; claudeResponse: number; total: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const answerCacheRef = useRef<Map<string, CachedAnswer>>(new Map());

  // Cleanup on unmount: abort controller, clear cache, reset state
  useEffect(() => {
    const cache = answerCacheRef.current;
    return () => {
      abortControllerRef.current?.abort();
      cache.clear();
      setQuestion('');
      setAnswer(null);
      setSources([]);
      setTokenUsage(null);
      setCost(null);
      setQueryTimeMs(null);
      setTiming(null);
      setError(null);
      setLoading(false);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || loading) return;

    // Abort any previous in-flight request
    abortControllerRef.current?.abort();

    const trimmedQuestion = question.trim();

    // Check cache first
    const cached = answerCacheRef.current.get(trimmedQuestion);
    if (cached) {
      setAnswer(cached.answer);
      setSources(cached.sources);
      setTokenUsage(cached.tokenUsage ?? null);
      setCost(cached.cost ?? null);
      setQueryTimeMs(cached.queryTimeMs ?? null);
      setTiming(cached.timing ?? null);
      setError(null);
      return;
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    setAnswer(null);
    setSources([]);
    setTokenUsage(null);
    setCost(null);
    setQueryTimeMs(null);
    setTiming(null);

    try {
      const startTime = performance.now();
      const result = await regulationService.askQuestion(trimmedQuestion, undefined, controller.signal);
      const elapsed = Math.round(performance.now() - startTime);
      setAnswer(result.answer);
      setSources(result.sources);
      if (result.tokenUsage) setTokenUsage(result.tokenUsage);
      if (result.cost !== undefined) setCost(result.cost);
      setQueryTimeMs(elapsed);
      if (result.timing) setTiming(result.timing);

      // Store in cache
      answerCacheRef.current.set(trimmedQuestion, {
        answer: result.answer,
        sources: result.sources,
        tokenUsage: result.tokenUsage,
        cost: result.cost,
        queryTimeMs: elapsed,
        timing: result.timing,
      });
    } catch (err: any) {
      // Silently ignore AbortError / CanceledError
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.response?.data?.message || 'Failed to get answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Abort any in-flight request, but do NOT reset state
    abortControllerRef.current?.abort();
    onClose();
  };

  /** Format document type for display */
  const formatDocumentType = (docType: string): string => {
    switch (docType) {
      case 'special_regulations':
        return 'Special Reg';
      case 'racing_rules':
        return 'Rule';
      case 'irc_rules':
        return 'IRC';
      case 'orci_rules':
        return 'ORCi';
      default:
        return docType;
    }
  };

  /** Get CSS class for document type badge */
  const getDocTypeBadgeClass = (docType: string): string => {
    if (docType === 'special_regulations') return 'badge-sr';
    if (docType === 'racing_rules') return 'badge-rrs';
    if (docType === 'irc_rules') return 'badge-irc';
    if (docType === 'orci_rules') return 'badge-orci';
    return 'badge-default';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content regulations-help-modal">
        <div className="modal-header">
          <h2>Ask About Rules and Regulations</h2>
          <button className="modal-close-btn" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit} className="regulations-question-form">
          <label htmlFor="question">
            Ask a question about the Racing Rules of Sailing or Australian Sailing Special Regulations:
          </label>
          <textarea
            id="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g., What life raft requirements apply to Category 2 races? Or: What does Rule 18 say about mark-room?"
            rows={3}
            maxLength={500}
            disabled={loading}
          />
          <div className="form-actions">
            <button type="submit" disabled={loading || !question.trim()}>
              {loading ? 'Thinking...' : 'Ask'}
            </button>
          </div>
        </form>

        {error && <div className="error-message">{error}</div>}

        {answer && (
          <div className="regulations-answer">
            <h3>Answer</h3>
            <div className="answer-text">{answer}</div>
            {sources.length > 0 && (
              <div className="answer-sources">
                <strong>Sources:</strong>
                <div className="source-badges">
                  {sources.map((source, index) => (
                    <span
                      key={index}
                      className={`source-badge ${getDocTypeBadgeClass(source.documentType)}`}
                    >
                      {formatDocumentType(source.documentType)} §{source.sectionNumber}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        <div className="modal-footer">
          <p className="disclaimer">
            Answers are AI-generated based on the uploaded Racing Rules and Special Regulations.
            Always verify critical requirements against the official documents.
          </p>
          {tokenUsage && (
            <p className="token-usage" style={{ fontSize: '0.75rem', color: '#888', marginTop: '8px' }}>
              Tokens: {tokenUsage.input.toLocaleString()} in / {tokenUsage.output.toLocaleString()} out / {tokenUsage.total.toLocaleString()} total
              {cost !== null && ` • Cost: $${cost.toFixed(4)}`}
              {queryTimeMs !== null && ` • ${(queryTimeMs / 1000).toFixed(1)}s`}
            </p>
          )}
          {timing && (
            <p className="timing-breakdown" style={{ fontSize: '0.7rem', color: '#aaa', marginTop: '4px' }}>
              Load: {timing.loadSections}s • Embed: {timing.generateEmbedding}s • Search: {timing.similaritySearch}s • Claude: {timing.claudeResponse}s • Backend: {timing.total}s
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegulationsHelpModal;
